import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  LinearProgress,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';

const ITEM_SORT_OPTIONS = [
  { value: 'quantity', label: 'Cantidad' },
  { value: 'client_name', label: 'Cliente' },
  { value: 'sku', label: 'SKU' },
  { value: 'status', label: 'Estado' },
  { value: 'size', label: 'Talle' },
  { value: 'variant', label: 'Color' }
];

const DetailItemsTable = ({
  title,
  items = [],
  paged,
  itemSearch,
  itemSortBy,
  itemSortDir,
  onPatch,
  statuses = {},
  checklist = {},
  checklistSummary,
  onToggleChecked,
  onIncrementReceived,
  onDecrementReceived,
  onMarkPageComplete,
  onClearPage,
  onConfirmPurchases,
  confirmingPurchases = false,
  lockedItemKeys,
  readOnlyChecklist = false
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const percent = checklistSummary?.ordered
    ? Math.round((Number(checklistSummary.received || 0) / Number(checklistSummary.ordered || 0)) * 100)
    : 0;
  const allComplete = Boolean(checklistSummary?.isComplete);
  const hasAnyReceived = Number(checklistSummary?.received || 0) > 0;
  const lockedSet = lockedItemKeys || new Set();

  const renderChecklistControls = (item) => {
    const key = item._rowKey;
    const entry = checklist[key] || { received: 0, checked: false };
    const expected = Number(item.quantity || 0);
    const isLocked = readOnlyChecklist || lockedSet.has(key);

    return (
      <Stack direction='row' alignItems='center' spacing={0.5}>
        <IconButton size='small' disabled={isLocked} onClick={() => onDecrementReceived(item)}>
          <RemoveRoundedIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <Chip
          size='small'
          color={isLocked || (entry.received >= expected && expected > 0) ? 'success' : 'default'}
          label={isLocked ? `Confirmado ${entry.received || expected}/${expected}` : `${entry.received}/${expected}`}
          sx={{ minWidth: 66 }}
        />
        <IconButton size='small' disabled={isLocked} onClick={() => onIncrementReceived(item)}>
          <AddRoundedIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <Checkbox
          size='small'
          checked={isLocked ? true : Boolean(entry.checked)}
          disabled={isLocked}
          onChange={() => onToggleChecked(item)}
        />
      </Stack>
    );
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        border: allComplete ? '1px solid #86efac' : '1px solid #e5e7eb',
        background: allComplete ? 'linear-gradient(120deg, #f0fdf4 0%, #ffffff 70%)' : '#fff',
        p: { xs: 1, sm: 1.2 }
      }}
    >
      <Stack direction={{ xs: 'column', md: 'row' }} gap={1} justifyContent='space-between' alignItems={{ xs: 'stretch', md: 'center' }}>
        <Box>
          <Typography sx={{ fontWeight: 800 }}>{title || 'Detalle de ítems'}</Typography>
          <Typography variant='caption' sx={{ color: 'text.secondary' }}>
            {paged.total} ítems
            {paged.totalPages > 1 ? `, página ${paged.page} de ${paged.totalPages}` : ''}
          </Typography>
        </Box>

        <Stack direction='row' gap={1} flexWrap='wrap'>
          <TextField
            size='small'
            label='Buscar en items'
            value={itemSearch}
            onChange={(event) => onPatch({ item_search: event.target.value, item_page: 1 })}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchRoundedIcon fontSize='small' />
                </InputAdornment>
              )
            }}
          />

          <FormControl size='small' sx={{ minWidth: 150 }}>
            <InputLabel id='item-sort-label'>Orden item</InputLabel>
            <Select
              labelId='item-sort-label'
              value={itemSortBy}
              label='Orden item'
              onChange={(event) => onPatch({ item_sort_by: event.target.value, item_page: 1 })}
            >
              {ITEM_SORT_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Tooltip title='Invertir orden'>
            <IconButton
              color='primary'
              onClick={() => onPatch({ item_sort_dir: itemSortDir === 'asc' ? 'desc' : 'asc', item_page: 1 })}
            >
              {itemSortDir === 'asc' ? <ArrowUpwardRoundedIcon /> : <ArrowDownwardRoundedIcon />}
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Paper
        elevation={0}
        sx={{
          mt: 1,
          borderRadius: 2,
          border: '1px solid #dbeafe',
          background: 'linear-gradient(120deg, #eff6ff 0%, #ffffff 100%)',
          p: 1
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent='space-between' alignItems={{ xs: 'stretch', sm: 'center' }} gap={1}>
          <Box>
            <Typography variant='caption' sx={{ color: 'text.secondary' }}>Checklist de carga efectiva</Typography>
            <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
              {checklistSummary?.completedItems || 0}/{checklistSummary?.totalItems || 0} ítems completos · {checklistSummary?.received || 0}/{checklistSummary?.ordered || 0} unidades
            </Typography>
          </Box>
          <Stack direction='row' gap={0.7}>
            {allComplete && <Chip size='small' color='success' label='Completo' />}
            <Button size='small' variant='outlined' onClick={onMarkPageComplete}>Completar página</Button>
            <Button size='small' variant='text' color='inherit' onClick={onClearPage} disabled={readOnlyChecklist}>Limpiar página</Button>
            <Button
              size='small'
              variant='contained'
              color='success'
              onClick={onConfirmPurchases}
              disabled={readOnlyChecklist || !hasAnyReceived || confirmingPurchases || !paged.items.length}
            >
              {confirmingPurchases ? 'Confirmando...' : 'Confirmar compras'}
            </Button>
          </Stack>
        </Stack>
        <LinearProgress
          variant='determinate'
          value={Math.max(0, Math.min(100, percent))}
          sx={{ mt: 1, height: 8, borderRadius: 999 }}
        />
      </Paper>

      <Stack direction='row' gap={0.6} flexWrap='wrap' mt={1}>
        {Object.entries(statuses).map(([status, quantity]) => (
          <Chip key={status} size='small' label={`${status}: ${quantity}`} variant='outlined' />
        ))}
      </Stack>

      {isMobile ? (
        <Stack mt={1} spacing={0.8}>
          {paged.items.map((item, idx) => (
            <Paper
              key={item._rowKey || `${item.id || item.sku || 'item'}-${idx}`}
              elevation={0}
              sx={{
                p: 1,
                borderRadius: 2,
                border: checklist[item._rowKey]?.checked ? '1px solid #86efac' : '1px solid #e5e7eb',
                background: checklist[item._rowKey]?.checked ? '#f0fdf4' : '#fff'
              }}
            >
              <Stack direction='row' justifyContent='space-between' gap={1}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }} noWrap>{item.description || item.sku || 'Ítem'}</Typography>
                  <Typography variant='caption' sx={{ color: 'text.secondary' }}>{item.client_name || '-'}</Typography>
                  <Stack direction='row' gap={0.5} mt={0.4} flexWrap='wrap'>
                    <Chip size='small' label={`SKU ${item.sku || '-'}`} variant='outlined' />
                    <Chip size='small' label={`Color ${item.variant || '-'}`} variant='outlined' />
                    <Chip size='small' label={`Talle ${item.size || '-'}`} variant='outlined' />
                    <Chip
                      size='small'
                      label={item.status || '-'}
                      color={(readOnlyChecklist || lockedSet.has(item._rowKey)) ? 'success' : 'default'}
                      variant={(readOnlyChecklist || lockedSet.has(item._rowKey)) ? 'filled' : 'outlined'}
                    />
                  </Stack>
                </Box>
              </Stack>
              <Box mt={0.8}>{renderChecklistControls(item)}</Box>
            </Paper>
          ))}
        </Stack>
      ) : (
        <TableContainer sx={{ mt: 1 }}>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Cliente</TableCell>
                <TableCell>SKU</TableCell>
                <TableCell>Descripción</TableCell>
                <TableCell>Color</TableCell>
                <TableCell>Talle</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align='right'>Cantidad</TableCell>
                <TableCell align='center'>Checklist</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paged.items.map((item, idx) => (
                <TableRow
                  key={item._rowKey || `${item.id || item.sku || 'item'}-${idx}`}
                  hover
                  sx={checklist[item._rowKey]?.checked ? { background: '#f0fdf4' } : undefined}
                >
                  <TableCell>{item.client_name || '-'}</TableCell>
                  <TableCell>{item.sku || '-'}</TableCell>
                  <TableCell>{item.description || '-'}</TableCell>
                  <TableCell>{item.variant || '-'}</TableCell>
                  <TableCell>{item.size || '-'}</TableCell>
                  <TableCell>{item.status || '-'}</TableCell>
                  <TableCell align='right'>{Number(item.quantity || 0)}</TableCell>
                  <TableCell align='center'>{renderChecklistControls(item)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!paged.items.length && (
        <Box sx={{ py: 2, textAlign: 'center', color: 'text.secondary' }}>
          <Typography>No hay ítems para los filtros actuales.</Typography>
        </Box>
      )}

      {paged.totalPages > 1 && (
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} justifyContent='space-between' alignItems={{ xs: 'flex-start', sm: 'center' }} mt={1}>
          <Typography variant='caption' sx={{ color: 'text.secondary' }}>
            Mostrando {paged.items.length} de {paged.total}
          </Typography>
          <Pagination
            page={paged.page}
            count={paged.totalPages}
            onChange={(_, nextPage) => onPatch({ item_page: nextPage })}
            color='primary'
            size='small'
          />
        </Stack>
      )}
    </Paper>
  );
};

export default DetailItemsTable;
